/**
 * ConsultasTab — Timeline cronológico de consultas del paciente.
 * Cada consulta es expandible y muestra el SOAP completo.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Chip, Accordion, AccordionSummary,
  AccordionDetails, CircularProgress, Grid, TextField,
  MenuItem, FormControl, InputLabel, Select,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  MedicalInformation as MedicalInfoIcon,
  Medication as MedicationIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  MonitorHeart as MonitorHeartIcon,
  Psychology as PsychologyIcon,
  Assignment as AssignmentIcon,
  Favorite as FavoriteIcon,
  Air as AirIcon,
  Thermostat as ThermostatIcon,
  Bloodtype as BloodtypeIcon,
  Speed as SpeedIcon,
  Scale as ScaleIcon,
  Height as HeightIcon,
  Opacity as OpacityIcon,
  // Exploración física (orden igual al formulario)
  Spa as SpaIcon,
  Face as FaceIcon,
  MonitorHeart as TorsoIcon,
  Adjust as AbdomenIcon,
  Wc as WcIcon,
  AccessibilityNew as ExtremidadesIcon,
  NoteAlt as NotesIcon,
  PostAdd as PostAddIcon,
} from '@mui/icons-material';
import { Button, IconButton, Tooltip } from '@mui/material';

const API_URL = '/api';

export default function ConsultasTab({ expediente, userId, onEditar, onNuevaNotaEvolucion, refreshKey = 0 }) {
  const [consultas, setConsultas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');

  useEffect(() => {
    if (!expediente?.id) return;
    (async () => {
      setCargando(true);
      try {
        const res = await fetch(`${API_URL}/emr/expedientes/${expediente.id}/completo`, {
          headers: { 'X-User-Id': userId },
        });
        const data = await res.json();
        const sorted = (data.consultas || []).sort(
          (a, b) => new Date(b.fecha_consulta) - new Date(a.fecha_consulta)
        );
        setConsultas(sorted);
      } catch (e) {
        console.error('Error cargando consultas', e);
      } finally {
        setCargando(false);
      }
    })();
  }, [expediente?.id, userId, refreshKey]);

  const consultasFiltradas = consultas.filter(c => {
    if (filtroEstado && c.estado !== filtroEstado) return false;
    if (filtroTexto) {
      const t = filtroTexto.toLowerCase();
      const txt = `${c.motivo_consulta || ''} ${c.padecimiento_actual || ''} ${c.impresion_diagnostica || ''}`.toLowerCase();
      if (!txt.includes(t)) return false;
    }
    return true;
  });

  if (cargando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Filtros */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth size="small"
            placeholder="Buscar por motivo, padecimiento o diagnóstico..."
            value={filtroTexto}
            onChange={(e) => setFiltroTexto(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: '#94a3b8', mr: 1 }} />,
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff' } }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff' } }}>
            <InputLabel shrink>Estado</InputLabel>
            <Select
              notched label="Estado" displayEmpty
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="en_proceso">En proceso</MenuItem>
              <MenuItem value="cerrada">Cerrada / firmada</MenuItem>
              <MenuItem value="cancelada">Cancelada</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Typography sx={{ mb: 1.5, fontSize: 12, color: '#64748b' }}>
        {consultasFiltradas.length} de {consultas.length} consulta{consultas.length === 1 ? '' : 's'}
      </Typography>

      {consultasFiltradas.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <MedicalInfoIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={{ color: '#64748b', fontSize: 14 }}>
            {consultas.length === 0 ? 'No hay consultas registradas' : 'No coinciden con los filtros'}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ position: 'relative', pl: 3 }}>
          {/* Línea vertical del timeline */}
          <Box sx={{
            position: 'absolute',
            left: 10, top: 8, bottom: 8,
            width: 2,
            background: 'linear-gradient(180deg, #6366f1 0%, #e2e8f0 100%)',
          }} />

          {(() => {
            // Identificar la consulta MÁS RECIENTE que sea firmada/cerrada (no borrador)
            // para mostrar el botón "+ Nota de evolución" solo en ella
            const ultimaCerrada = [...consultas]
              .filter(c => c.estado !== 'borrador' && c.estado !== 'cancelada')
              .sort((a, b) => new Date(b.fecha_consulta) - new Date(a.fecha_consulta))[0];
            return consultasFiltradas.map((c, i) => (
              <ConsultaCard
                key={c.id}
                consulta={c}
                index={i}
                onEditar={onEditar}
                onNuevaNotaEvolucion={
                  ultimaCerrada && ultimaCerrada.id === c.id ? onNuevaNotaEvolucion : null
                }
                consultasAnteriores={consultas.filter(o => {
                  // Incluir todas EXCEPTO la consulta actual (por id, no por fecha,
                  // así no se pierden consultas con fecha nula o del mismo día).
                  // Las anteriores estrictas se determinan por fecha al renderizar.
                  if (o.id === c.id) return false;
                  if (!o.fecha_consulta || !c.fecha_consulta) return true;
                  return new Date(o.fecha_consulta) <= new Date(c.fecha_consulta);
                })}
              />
            ));
          })()}
        </Box>
      )}
    </Box>
  );
}

function ConsultaCard({ consulta, index, onEditar, onNuevaNotaEvolucion, consultasAnteriores = [] }) {
  const [ordenes, setOrdenes] = useState([]);
  const [recetas, setRecetas] = useState([]);
  const [datosCargados, setDatosCargados] = useState(false);

  const cargarDatosExtra = async () => {
    if (datosCargados) return;
    const userId = localStorage.getItem('user_id');
    try {
      const [rOrdenes, rRecetas] = await Promise.all([
        fetch(`${API_URL}/emr/consultas/${consulta.id}/ordenes`, { headers: { 'X-User-Id': userId } }),
        fetch(`${API_URL}/emr/consultas/${consulta.id}/recetas`, { headers: { 'X-User-Id': userId } }),
      ]);
      if (rOrdenes.ok) {
        const d = await rOrdenes.json();
        setOrdenes(Array.isArray(d) ? d : (d.ordenes || []));
      }
      if (rRecetas.ok) {
        const d = await rRecetas.json();
        setRecetas(Array.isArray(d) ? d : (d.recetas || []));
      }
      setDatosCargados(true);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (index === 0) cargarDatosExtra();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fecha = new Date(consulta.fecha_consulta);
  const estado = consulta.estado || 'en_proceso';
  const estadoColor = {
    en_proceso: { bg: '#fef3c7', color: '#92400e', label: 'En proceso' },
    cerrada: { bg: '#d1fae5', color: '#065f46', label: 'Firmada' },
    cancelada: { bg: '#fee2e2', color: '#991b1b', label: 'Cancelada' },
  }[estado] || { bg: '#e2e8f0', color: '#475569', label: estado };

  const diagnosticoPrincipal = consulta.diagnosticos?.find(d => d.tipo_diagnostico === 'principal')
    || consulta.diagnosticos?.[0];

  return (
    <Box sx={{ position: 'relative', mb: 2 }}>
      {/* Punto del timeline */}
      <Box sx={{
        position: 'absolute',
        left: -23,
        top: 18,
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: '#6366f1',
        border: '3px solid #fff',
        boxShadow: '0 0 0 2px #6366f1',
      }} />

      <Accordion
        defaultExpanded={index === 0}
        onChange={(e, expanded) => { if (expanded) cargarDatosExtra(); }}
        sx={{
          borderRadius: '12px !important',
          boxShadow: 'none',
          border: '1px solid #e2e8f0',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6366f1', letterSpacing: 0.5 }}>
                  {fecha.toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })}
                </Typography>
                <Chip label={estadoColor.label} size="small"
                  sx={{ fontSize: 10, height: 18, background: estadoColor.bg, color: estadoColor.color, fontWeight: 700 }} />
                {consulta.modo_captura && consulta.modo_captura !== 'manual' && (
                  <Chip label={consulta.modo_captura === 'voz_ambient' ? '🎤 Ambient' : '🎤 Voz'} size="small"
                    sx={{ fontSize: 10, height: 18, background: '#ede9fe', color: '#5b21b6', fontWeight: 700 }} />
                )}
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                {consulta.motivo_consulta || '(sin motivo)'}
              </Typography>
              {diagnosticoPrincipal && (
                <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.3 }}>
                  Dx: <span style={{ fontWeight: 700 }}>{diagnosticoPrincipal.codigo_cie10}</span> {diagnosticoPrincipal.descripcion_cie10}
                </Typography>
              )}
            </Box>
            {onNuevaNotaEvolucion && (
              <Button
                size="small"
                variant="contained"
                startIcon={<PostAddIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => { e.stopPropagation(); onNuevaNotaEvolucion(); }}
                sx={{
                  textTransform: 'none', fontWeight: 700, fontSize: 12,
                  borderRadius: 2, flexShrink: 0, mr: 0.5,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  boxShadow: '0 3px 10px rgba(99, 102, 241, 0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    boxShadow: '0 5px 14px rgba(99, 102, 241, 0.45)',
                  }
                }}
              >
                + Nota de evolución
              </Button>
            )}
            {onEditar && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => { e.stopPropagation(); onEditar(consulta); }}
                sx={{
                  textTransform: 'none', fontWeight: 600, fontSize: 12,
                  borderColor: '#6366f1', color: '#6366f1', borderRadius: 2,
                  flexShrink: 0, mr: 0.5,
                  '&:hover': { background: '#eef2ff', borderColor: '#4f46e5' }
                }}
              >
                Editar consulta
              </Button>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ background: '#f8fafc' }}>
          <SeccionSOAP consulta={consulta} ordenes={ordenes} recetas={recetas} consultasAnteriores={consultasAnteriores} />
          {(onEditar || onNuevaNotaEvolucion) && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              {onNuevaNotaEvolucion && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PostAddIcon sx={{ fontSize: 16 }} />}
                  onClick={(e) => { e.stopPropagation(); onNuevaNotaEvolucion(); }}
                  sx={{
                    textTransform: 'none', fontWeight: 700, fontSize: 12, borderRadius: 2,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    boxShadow: '0 3px 10px rgba(99, 102, 241, 0.35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                      boxShadow: '0 5px 14px rgba(99, 102, 241, 0.45)',
                    }
                  }}
                >
                  + Nota de evolución
                </Button>
              )}
              {onEditar && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                  onClick={(e) => { e.stopPropagation(); onEditar(consulta); }}
                  sx={{
                    textTransform: 'none', fontWeight: 600, fontSize: 12,
                    borderColor: '#6366f1', color: '#6366f1', borderRadius: 2,
                    '&:hover': { background: '#eef2ff', borderColor: '#4f46e5' }
                  }}
                >
                  Editar consulta
                </Button>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

// ============================================================================
// SOAP — Estructura clínica estándar (Subjetivo · Objetivo · Análisis · Plan)
// ============================================================================

const SOAP_THEME = {
  S: { letter: 'S', label: 'Subjetivo', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', icon: PersonIcon, hint: 'Lo que cuenta el paciente' },
  O: { letter: 'O', label: 'Objetivo', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe', icon: MonitorHeartIcon, hint: 'Lo que medimos y observamos' },
  A: { letter: 'A', label: 'Análisis', color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0', icon: PsychologyIcon, hint: 'Lo que pensamos' },
  P: { letter: 'P', label: 'Plan', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', icon: AssignmentIcon, hint: 'Lo que hacemos' },
};

// Rangos de referencia clínicos (adulto) para signos vitales
function evalSignoVital(key, valor) {
  if (valor === null || valor === undefined || valor === '') return { estado: 'sin_dato' };
  const v = typeof valor === 'string' ? valor.trim() : valor;

  if (key === 'tension_arterial' || key === 'ta' || key === 'presion_arterial') {
    const m = String(v).match(/^(\d{2,3})\s*\/\s*(\d{2,3})$/);
    if (!m) return { estado: 'normal' };
    const sis = parseInt(m[1]), dia = parseInt(m[2]);
    if (sis < 90 || dia < 60) return { estado: 'bajo', mensaje: 'Hipotensión' };
    if (sis >= 140 || dia >= 90) return { estado: 'alto', mensaje: 'Hipertensión' };
    if (sis >= 130 || dia >= 80) return { estado: 'limite', mensaje: 'Elevada' };
    return { estado: 'normal' };
  }
  const n = parseFloat(v);
  if (isNaN(n)) return { estado: 'normal' };
  if (key === 'frecuencia_cardiaca' || key === 'fc') {
    if (n < 60) return { estado: 'bajo', mensaje: 'Bradicardia' };
    if (n > 100) return { estado: 'alto', mensaje: 'Taquicardia' };
    return { estado: 'normal' };
  }
  if (key === 'frecuencia_respiratoria' || key === 'fr') {
    if (n < 12) return { estado: 'bajo', mensaje: 'Bradipnea' };
    if (n > 20) return { estado: 'alto', mensaje: 'Taquipnea' };
    return { estado: 'normal' };
  }
  if (key === 'temperatura' || key === 'temp') {
    if (n < 36) return { estado: 'bajo', mensaje: 'Hipotermia' };
    if (n >= 38) return { estado: 'alto', mensaje: 'Fiebre' };
    if (n > 37.5) return { estado: 'limite', mensaje: 'Febrícula' };
    return { estado: 'normal' };
  }
  if (key === 'saturacion_oxigeno' || key === 'spo2' || key === 'sat_o2') {
    if (n < 90) return { estado: 'alto', mensaje: 'Hipoxia severa' };
    if (n < 95) return { estado: 'limite', mensaje: 'Hipoxia leve' };
    return { estado: 'normal' };
  }
  if (key === 'imc') {
    if (n < 18.5) return { estado: 'limite', mensaje: 'Bajo peso' };
    if (n >= 30) return { estado: 'alto', mensaje: 'Obesidad' };
    if (n >= 25) return { estado: 'limite', mensaje: 'Sobrepeso' };
    return { estado: 'normal' };
  }
  if (key === 'glucemia' || key === 'glucosa') {
    if (n < 70) return { estado: 'bajo', mensaje: 'Hipoglucemia' };
    if (n >= 126) return { estado: 'alto', mensaje: 'Hiperglucemia' };
    return { estado: 'normal' };
  }
  return { estado: 'normal' };
}

const SIGNO_META = {
  tension_arterial: { label: 'TA', unidad: 'mmHg', icon: BloodtypeIcon, color: '#ef4444' },
  frecuencia_cardiaca: { label: 'FC', unidad: 'lpm', icon: FavoriteIcon, color: '#ec4899' },
  frecuencia_respiratoria: { label: 'FR', unidad: 'rpm', icon: AirIcon, color: '#06b6d4' },
  temperatura: { label: 'T°', unidad: '°C', icon: ThermostatIcon, color: '#f97316' },
  saturacion_oxigeno: { label: 'SpO₂', unidad: '%', icon: OpacityIcon, color: '#3b82f6' },
  peso: { label: 'Peso', unidad: 'kg', icon: ScaleIcon, color: '#64748b' },
  talla: { label: 'Talla', unidad: 'm', icon: HeightIcon, color: '#64748b' },
  imc: { label: 'IMC', unidad: '', icon: SpeedIcon, color: '#8b5cf6' },
  glucemia: { label: 'Glucemia', unidad: 'mg/dL', icon: SpeedIcon, color: '#a855f7' },
  perimetro_cefalico: { label: 'P. cefálico', unidad: 'cm', icon: SpeedIcon, color: '#64748b' },
};

const ESTADO_COLORES = {
  normal: { bg: '#dcfce7', color: '#15803d', borde: '#86efac', label: 'Normal' },
  alto: { bg: '#fee2e2', color: '#b91c1c', borde: '#fca5a5', label: '↑' },
  bajo: { bg: '#dbeafe', color: '#1d4ed8', borde: '#93c5fd', label: '↓' },
  limite: { bg: '#fef3c7', color: '#a16207', borde: '#fcd34d', label: '⚠' },
  sin_dato: { bg: '#f1f5f9', color: '#94a3b8', borde: '#e2e8f0', label: '—' },
};

function SeccionSOAP({ consulta, ordenes = [], recetas: recetasProp = [], consultasAnteriores = [] }) {
  const recetasFinal = recetasProp.length > 0 ? recetasProp : (consulta.recetas || []);
  const sigVit = consulta.signos_vitales || {};
  const explF = consulta.exploracion_fisica || {};
  const intAS = consulta.interrogatorio_aparatos_sistemas;
  const evol = consulta.evolucion_problemas || {};
  const evolProblemas = Array.isArray(evol.problemas) ? evol.problemas : [];
  const tieneEvolucion = Boolean(
    evolProblemas.length > 0 || evol.evolucion_general || evol.adherencia_tratamiento
  );

  // ¿Hay datos en cada bloque SOAP? (para ocultar vacíos)
  const tieneSubjetivo = Boolean(
    consulta.motivo_consulta || consulta.padecimiento_actual || fmtObj(intAS) ||
    evol.evolucion_general || evol.adherencia_tratamiento
  );
  // tieneSignosVitales = hay valor actual O hay histórico para mostrar tendencia
  const hayActualVital = Object.values(sigVit).some(v => v !== null && v !== undefined && v !== '');
  const hayHistoricoVital = Array.isArray(consultasAnteriores) && consultasAnteriores.some(c => {
    const sv = c?.signos_vitales || {};
    return Object.values(sv).some(v => v !== null && v !== undefined && v !== '');
  });
  const tieneSignosVitales = hayActualVital || hayHistoricoVital;
  const tieneExpFisica = Boolean(fmtObj(explF));
  const tieneObjetivo = tieneSignosVitales || tieneExpFisica;
  const tieneAnalisis = Boolean(
    consulta.impresion_diagnostica || consulta.pronostico ||
    (consulta.diagnosticos && consulta.diagnosticos.length > 0) ||
    evolProblemas.length > 0
  );
  const tienePlan = Boolean(
    consulta.plan_tratamiento || consulta.indicaciones_paciente ||
    consulta.estudios_solicitados || consulta.interconsultas_solicitadas ||
    ordenes.length > 0 || recetasFinal.length > 0
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* S — Subjetivo */}
      {tieneSubjetivo && (
        <BloqueSOAP tema={SOAP_THEME.S} defaultOpen>
          {consulta.motivo_consulta && (
            <CampoTexto label="Motivo de consulta" valor={consulta.motivo_consulta} destacado />
          )}
          {consulta.padecimiento_actual && (
            <CampoTexto label="Padecimiento actual" valor={consulta.padecimiento_actual} />
          )}
          {evol.evolucion_general && (
            <CampoTexto label="Evolución desde la última consulta" valor={evol.evolucion_general} />
          )}
          {evol.adherencia_tratamiento && (
            <AdherenciaTratamientoBadge
              adherencia={evol.adherencia_tratamiento}
              motivo={evol.adherencia_motivo}
            />
          )}
          {fmtObj(intAS) && (
            <CampoTexto label="Interrogatorio por aparatos y sistemas" valor={fmtObj(intAS)} />
          )}
        </BloqueSOAP>
      )}

      {/* O — Objetivo (Exploración + estudios revisados) */}
      {(tieneObjetivo || (Array.isArray(evol.estudios_revisados) && evol.estudios_revisados.length > 0)) && (
        <BloqueSOAP tema={SOAP_THEME.O} defaultOpen>
          {tieneSignosVitales && (
            <SignosVitalesGrid
              signos={sigVit}
              consultasAnteriores={consultasAnteriores}
              consultaFechaActual={consulta.fecha_consulta}
              consultaEstadoActual={consulta.estado}
            />
          )}
          {tieneExpFisica && (
            <Box sx={{ mt: tieneSignosVitales ? 1.5 : 0 }}>
              <ExploracionFisicaView data={explF} />
            </Box>
          )}
          {Array.isArray(evol.estudios_revisados) && evol.estudios_revisados.length > 0 && (
            <Box sx={{ mt: (tieneSignosVitales || tieneExpFisica) ? 1.5 : 0 }}>
              <EstudiosRevisadosView estudios={evol.estudios_revisados} />
            </Box>
          )}
          {Array.isArray(evol.estudios_revisados) && evol.estudios_revisados.length > 0 && (
            <ArchivosAdjuntosView estudios={evol.estudios_revisados} />
          )}
        </BloqueSOAP>
      )}

      {/* A — Análisis */}
      {tieneAnalisis && (
        <BloqueSOAP tema={SOAP_THEME.A} defaultOpen>
          {consulta.impresion_diagnostica && (
            <CampoTexto label="Impresión diagnóstica" valor={consulta.impresion_diagnostica} destacado />
          )}
          {evolProblemas.length > 0 && (
            <EvolucionProblemasView problemas={evolProblemas} />
          )}
          {consulta.diagnosticos && consulta.diagnosticos.length > 0 && (
            <DiagnosticosCIE10 diagnosticos={consulta.diagnosticos} />
          )}
          {consulta.pronostico && (
            <CampoTexto label="Pronóstico" valor={consulta.pronostico} />
          )}
        </BloqueSOAP>
      )}

      {/* P — Plan */}
      {tienePlan && (
        <BloqueSOAP tema={SOAP_THEME.P} defaultOpen>
          {consulta.plan_tratamiento && (
            <CampoTexto label="Plan de tratamiento" valor={consulta.plan_tratamiento} />
          )}
          {consulta.indicaciones_paciente && (
            <CampoTexto label="Indicaciones al paciente" valor={consulta.indicaciones_paciente} />
          )}
          {consulta.estudios_solicitados && (
            <CampoTexto label="Estudios solicitados (texto)" valor={consulta.estudios_solicitados} />
          )}
          {consulta.interconsultas_solicitadas && (
            <CampoTexto label="Interconsultas solicitadas (texto)" valor={consulta.interconsultas_solicitadas} />
          )}
          {recetasFinal.length > 0 && <PrescripcionCard recetas={recetasFinal} />}
          {ordenes.length > 0 && <OrdenesCard ordenes={ordenes} />}
        </BloqueSOAP>
      )}

      {!tieneSubjetivo && !tieneObjetivo && !tieneAnalisis && !tienePlan && (
        <Box sx={{ p: 2, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
          Sin datos clínicos registrados.
        </Box>
      )}
    </Box>
  );
}

// Bloque SOAP individual (S/O/A/P)
function BloqueSOAP({ tema, children, defaultOpen = true }) {
  const [abierto, setAbierto] = useState(defaultOpen);
  const Icono = tema.icon;
  return (
    <Box sx={{
      borderRadius: 2,
      background: '#fff',
      border: `1px solid ${tema.border}`,
      overflow: 'hidden',
    }}>
      <Box
        onClick={() => setAbierto(!abierto)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
          px: 1.5,
          py: 1,
          cursor: 'pointer',
          background: tema.bg,
          borderBottom: abierto ? `1px solid ${tema.border}` : 'none',
          userSelect: 'none',
          '&:hover': { filter: 'brightness(0.97)' },
        }}
      >
        <Box sx={{
          width: 28, height: 28, borderRadius: '50%',
          background: tema.color, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 800,
          flexShrink: 0,
        }}>
          {tema.letter}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: tema.color, lineHeight: 1.2 }}>
            {tema.label}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: '#64748b', lineHeight: 1.2 }}>
            {tema.hint}
          </Typography>
        </Box>
        <Icono sx={{ fontSize: 18, color: tema.color, opacity: 0.6 }} />
        <ExpandMoreIcon sx={{
          fontSize: 20,
          color: tema.color,
          transform: abierto ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }} />
      </Box>
      {abierto && (
        <Box sx={{ px: 1.5, py: 1.2 }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

function CampoTexto({ label, valor, destacado = false, multilinea = false }) {
  if (!valor) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{
        fontSize: 9.5,
        fontWeight: 700,
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        mb: 0.3,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: destacado ? 13.5 : 12.5,
        fontWeight: destacado ? 600 : 400,
        color: '#1e293b',
        whiteSpace: 'pre-wrap',
        lineHeight: 1.5,
      }}>
        {valor}
      </Typography>
    </Box>
  );
}

// Sparkline SVG con marcadores, hover-tooltip y resaltado del punto actual
function Sparkline({ puntos = [], color = '#94a3b8', width = 80, height = 22, unidad = '' }) {
  if (!puntos || puntos.length < 2) return null;
  const valores = puntos.map(p => p.valor);
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  const range = max - min || 1;
  const padY = 3;
  const stepX = (width - 4) / (puntos.length - 1);
  const coords = puntos.map((p, i) => {
    const x = 2 + i * stepX;
    const y = height - padY - ((p.valor - min) / range) * (height - padY * 2);
    return { ...p, x, y };
  });
  const path = coords.map(({ x, y }, i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(' ');
  const areaPath = `${path} L${coords[coords.length - 1].x},${height} L${coords[0].x},${height} Z`;
  const idGrad = `grad-${Math.random().toString(36).slice(2, 7)}`;

  const fmtFecha = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('es', { day: '2-digit', month: 'short' });
  };
  const fmtEstado = (estado) => {
    if (!estado) return '';
    if (estado === 'cancelada') return ' · cancelada';
    if (estado === 'borrador') return ' · borrador';
    if (estado === 'en_proceso') return ' · en proceso';
    return '';
  };

  return (
    <svg width={width} height={height} style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={idGrad} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${idGrad})`} stroke="none" />
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {coords.map((p, i) => {
        // Reducir opacidad para puntos de consultas canceladas o borrador
        const esDudoso = p.estado === 'cancelada' || p.estado === 'borrador';
        return (
          <g key={i}>
            <circle
              cx={p.x} cy={p.y}
              r={p.esActual ? 3 : 1.9}
              fill={p.esActual ? color : '#fff'}
              stroke={color}
              strokeWidth={p.esActual ? 0 : 1.3}
              opacity={p.esActual ? 1 : (esDudoso ? 0.4 : 0.9)}
              strokeDasharray={esDudoso && !p.esActual ? '1.5 1' : '0'}
            >
              <title>{`${fmtFecha(p.fecha)}: ${p.valor}${unidad}${fmtEstado(p.estado)}`}</title>
            </circle>
          </g>
        );
      })}
    </svg>
  );
}

// Extrae el valor numérico de un signo vital (maneja TA "120/80" → toma sistólica)
function extraerNumero(key, valor) {
  if (valor === null || valor === undefined || valor === '') return null;
  if (key === 'tension_arterial' || key === 'ta' || key === 'presion_arterial') {
    const m = String(valor).match(/^(\d{2,3})\s*\/\s*\d{2,3}$/);
    return m ? parseInt(m[1]) : null;
  }
  const n = parseFloat(valor);
  return isNaN(n) ? null : n;
}

// Reúne valores históricos de un signo vital de TODAS las consultas dadas (con fecha)
// Devuelve [{valor, fecha, consulta_id, estado}] ordenado cronológicamente
function recolectarHistorico(key, consultas) {
  // Aliases ampliados — incluye TODAS las variantes que aparecen en BD:
  // 'sat' (sin sufijo), 'tension', 'presion', y combinaciones sis/dia
  const aliases = {
    // TA
    ta: 'tension_arterial',
    presion_arterial: 'tension_arterial',
    tension: 'tension_arterial',
    // FC
    fc: 'frecuencia_cardiaca',
    pulso: 'frecuencia_cardiaca',
    // FR
    fr: 'frecuencia_respiratoria',
    // Temperatura
    temp: 'temperatura',
    temperatura_celsius: 'temperatura',
    // Saturación O2 — bug original: faltaba 'sat'
    spo2: 'saturacion_oxigeno',
    sat: 'saturacion_oxigeno',
    sat_o2: 'saturacion_oxigeno',
    saturacion: 'saturacion_oxigeno',
    // Glucemia
    glucosa: 'glucemia',
    glucemia_capilar: 'glucemia',
  };
  const objetivo = key;
  const puntos = [];
  // Defensivo contra fechas nulas: las que no tengan fecha van al inicio
  const conFechaParseada = (consultas || []).map(c => ({
    c,
    ts: c.fecha_consulta ? new Date(c.fecha_consulta).getTime() : 0,
  })).sort((a, b) => a.ts - b.ts);

  for (const { c } of conFechaParseada) {
    const sv = c.signos_vitales || {};
    let v = null;
    // 1) Buscar por clave canónica
    if (sv[objetivo] !== undefined && sv[objetivo] !== null && sv[objetivo] !== '') {
      v = sv[objetivo];
    }
    // 2) Para TA específico: combinar sis/dia si existen
    if (v === null && objetivo === 'tension_arterial' &&
        sv.presion_arterial_sistolica && sv.presion_arterial_diastolica) {
      v = `${sv.presion_arterial_sistolica}/${sv.presion_arterial_diastolica}`;
    }
    // 3) Buscar por todos los aliases que apuntan al objetivo
    if (v === null) {
      for (const [alias, canon] of Object.entries(aliases)) {
        if (canon === objetivo && sv[alias] !== undefined && sv[alias] !== null && sv[alias] !== '') {
          v = sv[alias];
          break;
        }
      }
    }
    const num = extraerNumero(objetivo, v);
    if (num !== null) {
      puntos.push({
        valor: num,
        fecha: c.fecha_consulta,
        consulta_id: c.id,
        estado: c.estado || null,
      });
    }
  }
  return puntos;
}

// Signos vitales en grid con highlights
function SignosVitalesGrid({ signos, consultasAnteriores = [], consultaFechaActual = null, consultaEstadoActual = null }) {
  // Orden y entradas que tienen valor
  const ordenPreferido = [
    'tension_arterial', 'frecuencia_cardiaca', 'frecuencia_respiratoria',
    'temperatura', 'saturacion_oxigeno', 'peso', 'talla', 'imc',
    'perimetro_cefalico', 'glucemia',
  ];
  // Aliases ALINEADOS con recolectarHistorico (incluye 'sat', 'tension', 'pulso', etc.)
  const aliases = {
    ta: 'tension_arterial', presion_arterial: 'tension_arterial', tension: 'tension_arterial',
    fc: 'frecuencia_cardiaca', pulso: 'frecuencia_cardiaca',
    fr: 'frecuencia_respiratoria',
    temp: 'temperatura', temperatura_celsius: 'temperatura',
    spo2: 'saturacion_oxigeno', sat: 'saturacion_oxigeno', sat_o2: 'saturacion_oxigeno', saturacion: 'saturacion_oxigeno',
    glucosa: 'glucemia', glucemia_capilar: 'glucemia',
  };
  const norm = {};
  Object.entries(signos || {}).forEach(([k, v]) => {
    const key = aliases[k] || k;
    if (v !== null && v !== undefined && v !== '') norm[key] = v;
  });
  // Si TA viene partida (sistolica/diastolica) y no hay tension_arterial, combinar
  if (
    !norm.tension_arterial &&
    norm.presion_arterial_sistolica &&
    norm.presion_arterial_diastolica
  ) {
    norm.tension_arterial = `${norm.presion_arterial_sistolica}/${norm.presion_arterial_diastolica}`;
  }
  // Ocultar las claves crudas para que no aparezcan duplicadas en la grilla
  delete norm.presion_arterial_sistolica;
  delete norm.presion_arterial_diastolica;

  // Determinar qué signos tienen histórico aunque no estén en la consulta actual
  const clavesConHistorico = new Set();
  for (const claveCanon of ordenPreferido) {
    const histPts = recolectarHistorico(claveCanon, consultasAnteriores);
    if (histPts.length > 0) clavesConHistorico.add(claveCanon);
  }

  // Unir claves con valor actual + claves con histórico → mostrar todo
  const todasLasClaves = new Set([...Object.keys(norm), ...clavesConHistorico]);
  const keysOrdenadas = [
    ...ordenPreferido.filter(k => todasLasClaves.has(k)),
    ...Array.from(todasLasClaves).filter(k => !ordenPreferido.includes(k)),
  ];
  if (keysOrdenadas.length === 0) return null;

  return (
    <Box>
      <Typography sx={{
        fontSize: 9.5, fontWeight: 700, color: '#64748b',
        textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.7,
      }}>
        Signos vitales
      </Typography>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
        gap: 0.8,
      }}>
        {keysOrdenadas.map(key => {
          const meta = SIGNO_META[key] || { label: key, unidad: '', icon: SpeedIcon, color: '#64748b' };
          const tieneActual = norm[key] !== undefined && norm[key] !== '';
          const evalua = tieneActual ? evalSignoVital(key, norm[key]) : { estado: 'sin_dato' };
          const colores = ESTADO_COLORES[evalua.estado] || ESTADO_COLORES.normal;
          const Ico = meta.icon;
          return (
            <Box key={key} sx={{
              p: 0.8,
              borderRadius: 1.5,
              border: `1px solid ${tieneActual ? colores.borde : '#e2e8f0'}`,
              background: tieneActual ? '#fff' : '#fafbfc',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.2,
              opacity: tieneActual ? 1 : 0.85,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Ico sx={{ fontSize: 12, color: meta.color }} />
                <Typography sx={{ fontSize: 9, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                  {meta.label}
                </Typography>
                {evalua.estado !== 'normal' && evalua.estado !== 'sin_dato' && (
                  <Box sx={{
                    ml: 'auto', px: 0.4, borderRadius: 0.5,
                    background: colores.bg, color: colores.color,
                    fontSize: 9, fontWeight: 800, lineHeight: 1.4,
                  }}>
                    {colores.label}
                  </Box>
                )}
              </Box>
              <Typography sx={{
                fontSize: 14, fontWeight: 700,
                color: !tieneActual ? '#cbd5e1' : (evalua.estado === 'normal' ? '#0f172a' : colores.color),
              }}>
                {tieneActual ? norm[key] : '—'}{meta.unidad && <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500, marginLeft: 2 }}>{meta.unidad}</span>}
              </Typography>
              {evalua.mensaje && (
                <Typography sx={{ fontSize: 9, color: colores.color, fontWeight: 600 }}>
                  {evalua.mensaje}
                </Typography>
              )}
              {/* Sparkline tendencia histórica con TODOS los puntos del expediente */}
              {(() => {
                const histPrevios = recolectarHistorico(key, consultasAnteriores);
                const actualNum = extraerNumero(key, norm[key]);
                // Construir la serie completa: históricos + actual marcado como "esActual"
                const serie = [...histPrevios];
                if (actualNum !== null) {
                  serie.push({
                    valor: actualNum,
                    fecha: consultaFechaActual,
                    estado: consultaEstadoActual,
                    esActual: true,
                  });
                }
                if (serie.length < 2) {
                  if (serie.length === 1) {
                    return (
                      <Typography sx={{ fontSize: 8, color: '#cbd5e1', fontStyle: 'italic', mt: 0.3 }}>
                        Sin tendencia · 1 medición
                      </Typography>
                    );
                  }
                  return null;
                }
                const previo = histPrevios.length > 0 ? histPrevios[histPrevios.length - 1].valor : null;
                const delta = previo !== null && actualNum !== null ? actualNum - previo : null;
                const flecha = delta === null ? '' : delta > 0 ? '▲' : delta < 0 ? '▼' : '–';
                const colorDelta = delta === null ? '#94a3b8' : delta > 0 ? '#ef4444' : delta < 0 ? '#0ea5e9' : '#94a3b8';
                const totalCanceladas = serie.filter(p => p.estado === 'cancelada').length;
                return (
                  <Box sx={{ mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.2 }}>
                      <Typography sx={{ fontSize: 8, color: '#94a3b8', fontWeight: 600, letterSpacing: 0.3 }}>
                        Tendencia · {serie.length}{totalCanceladas > 0 ? ` (${totalCanceladas} cancel.)` : ''}
                      </Typography>
                      {delta !== null && delta !== 0 && (
                        <Typography sx={{ fontSize: 8.5, fontWeight: 700, color: colorDelta }}>
                          {flecha} {Math.abs(delta).toFixed(delta % 1 === 0 ? 0 : 1)}
                        </Typography>
                      )}
                    </Box>
                    <Sparkline puntos={serie} color={meta.color} width={70} height={18} unidad={meta.unidad} />
                  </Box>
                );
              })()}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function DiagnosticosCIE10({ diagnosticos }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{
        fontSize: 9.5, fontWeight: 700, color: '#64748b',
        textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.5,
      }}>
        Diagnósticos CIE-10 ({diagnosticos.length})
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {diagnosticos.map((d, i) => {
          const esPrincipal = d.tipo_diagnostico === 'principal';
          return (
            <Chip
              key={i}
              size="small"
              label={
                <span>
                  <b>{d.codigo_cie10}</b> · {d.descripcion_cie10}
                  {esPrincipal && <span style={{ marginLeft: 6, fontSize: 9, opacity: 0.85 }}>· PRINCIPAL</span>}
                </span>
              }
              sx={{
                background: esPrincipal ? '#dcfce7' : '#f1f5f9',
                color: esPrincipal ? '#15803d' : '#334155',
                border: esPrincipal ? '1px solid #86efac' : '1px solid #e2e8f0',
                fontWeight: 600,
                fontSize: 11,
                height: 24,
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

function PrescripcionCard({ recetas }) {
  return (
    <Box sx={{
      mt: 1, p: 1.2, borderRadius: 1.5,
      background: '#faf5ff', border: '1px solid #e9d5ff',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.7 }}>
        <MedicationIcon sx={{ fontSize: 15, color: '#8b5cf6' }} />
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#581c87', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Prescripción ({recetas.length} {recetas.length === 1 ? 'receta' : 'recetas'})
        </Typography>
      </Box>
      {recetas.map((rx, i) => {
        const meds = Array.isArray(rx.medicamentos) ? rx.medicamentos : [];
        return (
          <Box key={i} sx={{ mb: i < recetas.length - 1 ? 0.7 : 0 }}>
            {recetas.length > 1 && (
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#a855f7', textTransform: 'uppercase', mt: 0.3 }}>
                Receta {i + 1}
              </Typography>
            )}
            {meds.map((m, j) => (
              <Typography key={`${i}-${j}`} sx={{ fontSize: 12, color: '#581c87', mb: 0.2 }}>
                • <b>{m.nombre}</b>
                {(m.dosis_mg || m.dosis) && ` ${m.dosis_mg || m.dosis}${m.dosis_mg ? 'mg' : ''}`}
                {(m.via || m.via_administracion) && ` vía ${m.via || m.via_administracion}`}
                {m.frecuencia_horas && ` c/${m.frecuencia_horas}h`}
                {m.frecuencia && ` ${m.frecuencia}`}
                {(m.duracion_dias || m.duracion) && ` × ${m.duracion_dias || m.duracion}${m.duracion_dias ? ' días' : ''}`}
                {m.indicaciones && (
                  <span style={{ display: 'block', fontSize: 11, color: '#7c3aed', fontStyle: 'italic', marginLeft: 12 }}>
                    {m.indicaciones}
                  </span>
                )}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

function OrdenesCard({ ordenes }) {
  const TIPO_ORDEN_LABELS = {
    laboratorio: { label: 'Orden de laboratorio', emoji: '🧪', color: '#06b6d4', bg: '#ecfeff' },
    interconsulta: { label: 'Interconsulta', emoji: '🏥', color: '#0ea5e9', bg: '#eff6ff' },
    hospitalizacion: { label: 'Hospitalización', emoji: '🛏', color: '#ef4444', bg: '#fef2f2' },
    certificado: { label: 'Certificado médico', emoji: '📋', color: '#10b981', bg: '#ecfdf5' },
    justificante: { label: 'Justificante médico', emoji: '📅', color: '#f59e0b', bg: '#fffbeb' },
    libre: { label: 'Orden libre', emoji: '✏', color: '#64748b', bg: '#f8fafc' },
  };
  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{
        fontSize: 10, fontWeight: 700, color: '#64748b',
        textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5,
      }}>
        Documentos generados ({ordenes.length})
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {ordenes.map(o => {
          const meta = TIPO_ORDEN_LABELS[o.tipo] || TIPO_ORDEN_LABELS.libre;
          return (
            <Box key={o.id} sx={{
              p: 1, borderRadius: 1.5,
              background: meta.bg,
              border: `1px solid ${meta.color}33`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                <span style={{ fontSize: 13 }}>{meta.emoji}</span>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: meta.color, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  {meta.label}
                </Typography>
              </Box>
              {o.tipo === 'laboratorio' && o.contenido?.estudios && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                  {o.contenido.estudios.map((e, i) => (
                    <Chip key={i} label={e.nombre} size="small" sx={{ fontSize: 10, height: 18, background: '#fff' }} />
                  ))}
                </Box>
              )}
              {o.tipo === 'interconsulta' && (
                <Typography sx={{ fontSize: 11.5, color: '#0c4a6e' }}>
                  <b>{o.contenido?.especialidad}</b>
                  {o.contenido?.motivo && ` — ${o.contenido.motivo.substring(0, 140)}`}
                </Typography>
              )}
              {['hospitalizacion', 'certificado', 'justificante', 'libre'].includes(o.tipo) && (
                <Typography sx={{ fontSize: 11.5, color: '#1e293b' }}>
                  {(o.contenido?.indicaciones_hospitalarias || o.contenido?.diagnostico || o.contenido?.texto || '').substring(0, 200)}
                </Typography>
              )}
              {o.indicaciones && (
                <Typography sx={{ fontSize: 10.5, color: '#64748b', mt: 0.3, fontStyle: 'italic' }}>
                  {o.indicaciones}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ============================================================================
// Nota de evolución (POMR)
// ============================================================================
const ADHERENCIA_META = {
  total:   { label: 'Adherencia total',   color: '#10b981', bg: '#ecfdf5', borde: '#86efac', emoji: '✅' },
  parcial: { label: 'Adherencia parcial', color: '#f59e0b', bg: '#fffbeb', borde: '#fde68a', emoji: '⚠️' },
  no:      { label: 'No adherido',        color: '#ef4444', bg: '#fef2f2', borde: '#fca5a5', emoji: '❌' },
};

function AdherenciaTratamientoBadge({ adherencia, motivo }) {
  const meta = ADHERENCIA_META[adherencia];
  if (!meta) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{
        fontSize: 9.5, fontWeight: 700, color: '#64748b',
        textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.4,
      }}>
        Adherencia al tratamiento
      </Typography>
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.6,
        px: 1, py: 0.4,
        borderRadius: 1.5,
        background: meta.bg,
        border: `1px solid ${meta.borde}`,
      }}>
        <span style={{ fontSize: 13 }}>{meta.emoji}</span>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: meta.color }}>
          {meta.label}
        </Typography>
      </Box>
      {motivo && (
        <Typography sx={{ fontSize: 11.5, color: '#475569', mt: 0.4, fontStyle: 'italic' }}>
          {motivo}
        </Typography>
      )}
    </Box>
  );
}

const ESTADO_EVOL_META = {
  mejor:    { label: 'Mejor',    color: '#10b981', bg: '#ecfdf5', borde: '#86efac', emoji: '↗' },
  igual:    { label: 'Igual',    color: '#64748b', bg: '#f1f5f9', borde: '#cbd5e1', emoji: '→' },
  peor:     { label: 'Peor',     color: '#ef4444', bg: '#fef2f2', borde: '#fca5a5', emoji: '↘' },
  resuelto: { label: 'Resuelto', color: '#0ea5e9', bg: '#eff6ff', borde: '#93c5fd', emoji: '✓' },
  nuevo:    { label: 'Nuevo',    color: '#a855f7', bg: '#faf5ff', borde: '#d8b4fe', emoji: '★' },
};

function EvolucionProblemasView({ problemas }) {
  if (!Array.isArray(problemas) || problemas.length === 0) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{
        fontSize: 9.5, fontWeight: 700, color: '#64748b',
        textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.6,
      }}>
        Evolución de problemas activos ({problemas.length})
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        {problemas.map((p, idx) => {
          const meta = ESTADO_EVOL_META[p.estado] || ESTADO_EVOL_META.igual;
          return (
            <Box key={`${p.codigo_cie10}-${idx}`} sx={{
              p: 0.8,
              borderRadius: 1.5,
              background: meta.bg,
              border: `1px solid ${meta.borde}`,
              borderLeft: `3px solid ${meta.color}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.3,
                  px: 0.6, py: 0.1, borderRadius: 1,
                  background: meta.color, color: '#fff',
                  fontSize: 10, fontWeight: 800, lineHeight: 1.4,
                  textTransform: 'uppercase', letterSpacing: 0.4,
                }}>
                  <span>{meta.emoji}</span>
                  <span>{meta.label}</span>
                </Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>
                  <span style={{ color: meta.color }}>{p.codigo_cie10}</span> · {p.descripcion}
                </Typography>
              </Box>
              {p.comentario && (
                <Typography sx={{ fontSize: 12, color: '#334155', mt: 0.3, fontStyle: 'italic' }}>
                  {p.comentario}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// Estudios traídos por el paciente (con OCR estructurado)
function EstudiosRevisadosView({ estudios }) {
  if (!Array.isArray(estudios) || estudios.length === 0) return null;
  const estadoColor = (estado) => {
    if (estado === 'alto') return { bg: '#fef2f2', color: '#b91c1c', borde: '#fca5a5', flecha: '↑' };
    if (estado === 'bajo') return { bg: '#eff6ff', color: '#1d4ed8', borde: '#93c5fd', flecha: '↓' };
    return { bg: '#ecfdf5', color: '#15803d', borde: '#86efac', flecha: '✓' };
  };
  return (
    <Box>
      <Typography sx={{
        fontSize: 9.5, fontWeight: 700, color: '#0891b2',
        textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.7,
      }}>
        Estudios revisados ({estudios.length})
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
        {estudios.map((est, i) => {
          const params = est.parametros || [];
          const anormales = params.filter(p => p.estado === 'alto' || p.estado === 'bajo').length;
          return (
            <Box key={est._id || i} sx={{
              p: 0.9, borderRadius: 1.5,
              background: '#fff',
              border: '1px solid #cffafe',
              borderLeft: anormales > 0 ? '3px solid #ef4444' : '3px solid #06b6d4',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#0c4a6e', flex: 1, minWidth: 150 }}>
                  🧪 {est.nombre || 'Estudio'}
                </Typography>
                {est.fecha && (
                  <Chip label={est.fecha} size="small"
                    sx={{ fontSize: 9.5, height: 18, background: '#eff6ff', color: '#1e3a8a', fontWeight: 700 }} />
                )}
                {anormales > 0 && (
                  <Chip label={`${anormales} fuera de rango`} size="small"
                    sx={{ fontSize: 9.5, height: 18, background: '#fef2f2', color: '#b91c1c', fontWeight: 700 }} />
                )}
              </Box>
              {params.length > 0 && (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                  gap: 0.4,
                }}>
                  {params.map((p, j) => {
                    const c = estadoColor(p.estado);
                    return (
                      <Box key={j} sx={{
                        p: 0.5, borderRadius: 1,
                        background: c.bg,
                        border: `1px solid ${c.borde}`,
                      }}>
                        <Typography sx={{ fontSize: 9.5, color: '#475569', fontWeight: 600 }}>
                          {p.nombre}
                        </Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: c.color }}>
                          {p.valor}{p.unidad && <span style={{ fontSize: 9, opacity: 0.7, marginLeft: 2 }}>{p.unidad}</span>}
                          {(p.estado === 'alto' || p.estado === 'bajo') && (
                            <span style={{ marginLeft: 4, fontSize: 11 }}>{c.flecha}</span>
                          )}
                        </Typography>
                        {p.referencia && (
                          <Typography sx={{ fontSize: 8.5, color: '#94a3b8' }}>
                            Ref: {p.referencia}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
              {est.interpretacion_breve && (
                <Typography sx={{ fontSize: 11.5, color: '#0c4a6e', mt: 0.5, fontStyle: 'italic', pl: 0.5, borderLeft: '2px solid #67e8f9' }}>
                  {est.interpretacion_breve}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// Archivos adjuntos del paciente (PDFs/imágenes subidos en notas de evolución)
function ArchivosAdjuntosView({ estudios }) {
  if (!Array.isArray(estudios) || estudios.length === 0) return null;
  // Deduplicar por archivo_url (un mismo archivo puede haber generado varios estudios)
  const archivosMap = new Map();
  for (const est of estudios) {
    if (!est.archivo_url) continue;
    if (!archivosMap.has(est.archivo_url)) {
      archivosMap.set(est.archivo_url, {
        url: est.archivo_url,
        nombre: est.archivo_nombre || 'Archivo',
        tipo: est.archivo_tipo || 'application/octet-stream',
        cantidad: 1,
      });
    } else {
      archivosMap.get(est.archivo_url).cantidad += 1;
    }
  }
  const archivos = [...archivosMap.values()];
  if (archivos.length === 0) return null;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography sx={{
        fontSize: 9.5, fontWeight: 700, color: '#64748b',
        textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.6,
      }}>
        📎 Archivos adjuntos del paciente ({archivos.length})
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {archivos.map((a, i) => {
          const esPdf = a.tipo === 'application/pdf';
          return (
            <Box
              key={i}
              component="a"
              href={`${API_URL}${a.url}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.7,
                p: 0.8, borderRadius: 1.5,
                background: esPdf
                  ? 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)'
                  : 'linear-gradient(135deg, #ecfeff 0%, #eff6ff 100%)',
                border: `1px solid ${esPdf ? '#fecaca' : '#a5f3fc'}`,
                textDecoration: 'none',
                transition: 'all 0.15s',
                cursor: 'pointer',
                '&:hover': {
                  filter: 'brightness(0.97)',
                  boxShadow: `0 2px 8px ${esPdf ? 'rgba(239, 68, 68, 0.15)' : 'rgba(8, 145, 178, 0.15)'}`,
                },
              }}
            >
              <Box sx={{ fontSize: 18, lineHeight: 1 }}>
                {esPdf ? '📄' : '🖼️'}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontSize: 12, fontWeight: 700,
                  color: esPdf ? '#991b1b' : '#0c4a6e',
                  textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
                }}>
                  {a.nombre}
                </Typography>
                <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                  {a.cantidad} estudio{a.cantidad !== 1 ? 's' : ''} extraído{a.cantidad !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Typography sx={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                color: esPdf ? '#dc2626' : '#0891b2',
              }}>
                Abrir →
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// Mismo orden, color e ícono que ExploracionFisica.jsx (formulario)
const SECCIONES_EXP = [
  { key: 'piel_faneras', label: 'Piel y faneras', color: '#f97316', icon: SpaIcon },
  { key: 'cabeza_cuello', label: 'Cabeza y cuello', color: '#ec4899', icon: FaceIcon },
  { key: 'torax', label: 'Tórax', color: '#0ea5e9', icon: TorsoIcon },
  { key: 'abdomen', label: 'Abdomen', color: '#f59e0b', icon: AbdomenIcon },
  { key: 'genitales', label: 'Genitales', color: '#14b8a6', icon: WcIcon },
  { key: 'extremidades', label: 'Extremidades', color: '#10b981', icon: ExtremidadesIcon },
  { key: 'neurologico', label: 'Neurológico', color: '#8b5cf6', icon: PsychologyIcon },
  { key: 'observaciones', label: 'Observaciones', color: '#64748b', icon: NotesIcon },
];

function ExploracionFisicaView({ data }) {
  if (!data || typeof data !== 'object') return null;
  const conDatos = SECCIONES_EXP.filter(s => {
    const v = data[s.key];
    return v && typeof v === 'string' && v.trim().length > 0;
  });
  // Claves no estándar (datos antiguos o de otros formatos)
  const claveSet = new Set(SECCIONES_EXP.map(s => s.key));
  const extras = Object.entries(data).filter(([k, v]) =>
    !claveSet.has(k) && v && typeof v === 'string' && v.trim().length > 0
  );

  if (conDatos.length === 0 && extras.length === 0) return null;

  return (
    <Box>
      <Typography sx={{
        fontSize: 9.5, fontWeight: 700, color: '#64748b',
        textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.7,
      }}>
        Exploración física
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
        {conDatos.map(s => {
          const Ico = s.icon;
          return (
            <Box key={s.key} sx={{
              display: 'flex',
              gap: 1,
              p: 1,
              borderRadius: 1.5,
              background: '#fff',
              border: `1px solid ${s.color}33`,
              borderLeft: `3px solid ${s.color}`,
              transition: 'all 0.2s',
              '&:hover': {
                background: `${s.color}08`,
              },
            }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: 1.5,
                flexShrink: 0,
                background: `${s.color}15`,
                color: s.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Ico sx={{ fontSize: 16 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: s.color,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  lineHeight: 1.3,
                  mb: 0.3,
                }}>
                  {s.label}
                </Typography>
                <Typography sx={{
                  fontSize: 12.5,
                  color: '#1e293b',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.5,
                }}>
                  {data[s.key]}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {/* Claves no estándar (datos legacy) */}
        {extras.length > 0 && (
          <Box sx={{
            p: 1, borderRadius: 1.5,
            background: '#f8fafc',
            border: '1px dashed #cbd5e1',
            borderLeft: '3px solid #94a3b8',
          }}>
            <Typography sx={{
              fontSize: 10, fontWeight: 700, color: '#64748b',
              textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3,
            }}>
              Otros hallazgos
            </Typography>
            {extras.map(([k, v]) => (
              <Typography key={k} sx={{ fontSize: 12, color: '#1e293b', mb: 0.3 }}>
                <b style={{ color: '#475569' }}>{k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</b> {v}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function fmtObj(obj) {
  if (!obj || (typeof obj === 'object' && Object.keys(obj).length === 0)) return '';
  if (typeof obj === 'string') return obj;
  if (typeof obj === 'object') {
    // Si solo tiene la key 'texto' (caso interrogatorio), mostrar solo el value
    const keys = Object.keys(obj).filter(k => obj[k] !== null && obj[k] !== undefined && obj[k] !== '');
    if (keys.length === 1 && keys[0] === 'texto') return obj.texto;
    return keys
      .map(k => {
        // Formato bonito para keys conocidas (capitalizadas, snake_case → espacios)
        const labelKey = k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        return `${labelKey}: ${obj[k]}`;
      })
      .join('\n');
  }
  return String(obj);
}
